// Shared, public-page media renderer for the `library` block. Given an already
// PUBLIC media src + its filename/mime, it dispatches on getPreviewKind (the SAME
// classification the Library pane uses) to the matching viewer, REUSING the
// library-pane 3D viewer (ModelPreview) so we don't ship a second Three.js path.
//
// Used in TWO places so there's one preview path:
//   • file-mode library leaf — renders the single picked file inline.
//   • folder-mode browser lightbox — renders a file the visitor clicked.
import React, { Suspense, lazy, useEffect, useRef } from 'react';
import { getPreviewKind, mediaIdFromSrc, thumbUrlFromMediaSrc } from '@/utils/fileUtils';
import { WaveformAudioPlayer } from '@/components/common/WaveformAudioPlayer';
import { attachRobustAutoplay } from '@/utils/robustVideoAutoplay';
import { useProgressiveTierSrc, useProgressiveTierVideoRef } from '../useProgressiveTierSrc';
import type { MediaUpload } from '@/types/mediaUpload';
import { isSafeSiteUrl } from '../../../../shared/pageBlocks';

// Heavy (Three.js loaders/controls) — only pages with a 3D file pay for it.
const ModelPreview = lazy(() => import('@/components/LibraryPanel/previews/ModelPreview'));
const SlideDeckPreview = lazy(() => import('@/components/LibraryPanel/slides/SlideDeckPreview'));

export interface LibraryMediaViewProps {
  src: string;
  filename: string;
  mime: string;
  /** Render box in CSS px. */
  width: number;
  height: number;
  /** 3D only. */
  autoRotate?: boolean;
  bgColor?: string;
  /** Video only — playback options (controls defaults on). */
  videoAutoPlay?: boolean;
  videoLoop?: boolean;
  videoMuted?: boolean;
  videoControls?: boolean;
  /** Tiny (~24px) plaintext blur-up placeholder — shown instantly, before
   *  `variantSrc` even resolves. Always CDN-free-servable (unlike the
   *  classic 96px Library thumb, which is always encrypted on B2 and can't
   *  be redirected to the CDN Worker without a droplet-side decrypt).
   *  Absent = not generated yet — falls back to no placeholder, identical
   *  to before this tier existed. */
  variantPlaceholderSrc?: string;
  /** Small (~960px) WebP/JPEG image variant, shown immediately once loaded,
   *  when a ready one exists server-side (server/mediaCdn.js
   *  attachSiteMediaVariants). Absent = CDN off, item not yet processed, or
   *  Worker not deployed — the <img> just renders `src` alone, identical to
   *  today. */
  variantSrc?: string;
  /** Large (~1600px) WebP/JPEG tier — requested only after every other
   *  small-tier image on the page has settled (useProgressiveTierSrc). Same
   *  absent-is-fine contract as variantSrc. */
  variantLgSrc?: string;
  /** Re-encoded low-bitrate MP4 variant, preferred over the original on narrow
   *  render boxes. Same absent-is-fine contract as variantSrc. */
  videoVariantSrc?: string;
  /** Larger (~640px) video poster JPEG variant; falls back to the existing 96px
   *  thumb when absent. */
  posterVariantSrc?: string;
  /** True for the page's likely LCP element (first leaf, first row/section) —
   *  loads eager/high-priority instead of the lazy/auto default. */
  priority?: boolean;
  /** Image alt text. Falls back to `filename` (today's behavior) when absent
   *  — every existing caller that doesn't pass this is unaffected. */
  alt?: string;
}

const boxStyle = (w: number, h: number): React.CSSProperties =>
  ({ width: w, height: h, maxWidth: '100%', borderRadius: 8, overflow: 'hidden' });

const Centered: React.FC<{ children: React.ReactNode; h: number }> = ({ children, h }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: h, color: '#a1a1aa', fontSize: 13 }}>
    {children}
  </div>
);

// Autoplay site-builder videos need the same safeguards as the feed/renderer
// preview: browsers reject a naive `play()` under their autoplay policy or a
// cold-cache readiness race, and a page can stack several of these blocks at
// once (a "liked videos" gallery), which makes both failure modes common. A
// plain `<video autoPlay>` swallows that rejection silently, leaving a black,
// frozen poster-less frame. `attachRobustAutoplay` retries on readiness events
// and on the next user gesture; the IntersectionObserver keeps only videos
// actually on screen attempting to play, rather than every block on the page
// fighting over the browser's autoplay allowance at once.
// `videoRef` comes from useProgressiveTierVideoRef (called once, unconditionally,
// in LibraryMediaView below) — it owns `.src` imperatively for the seamless
// small→original swap, so this component never touches `src` itself; `depKey`
// (the item's `original` source) re-attaches the autoplay/IntersectionObserver
// wiring whenever the underlying item actually changes.
const AutoplayVideo: React.FC<{ videoRef: React.RefObject<HTMLVideoElement>; depKey: string; poster?: string; controls: boolean; loop?: boolean; muted: boolean; style: React.CSSProperties }> =
  ({ videoRef, depKey, poster, controls, loop, muted, style }) => {
    const visibleRef = useRef(typeof IntersectionObserver === 'undefined');

    useEffect(() => {
      const el = videoRef.current;
      if (!el) return;
      const detach = attachRobustAutoplay(el, () => visibleRef.current);
      if (typeof IntersectionObserver === 'undefined') return detach;
      const io = new IntersectionObserver((entries) => {
        const visible = entries.some((e) => e.isIntersecting);
        visibleRef.current = visible;
        if (visible) el.play().catch(() => {});
        else el.pause();
      }, { threshold: 0.25 });
      io.observe(el);
      return () => { detach(); io.disconnect(); };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [depKey]);

    return <video ref={videoRef} poster={poster} controls={controls} loop={loop} muted={muted} playsInline preload="metadata" style={style} />;
  };

export const LibraryMediaView: React.FC<LibraryMediaViewProps> = ({
  src, filename, mime, width, height, autoRotate, bgColor,
  videoAutoPlay, videoLoop, videoMuted, videoControls = true,
  variantPlaceholderSrc, variantSrc, variantLgSrc, videoVariantSrc, posterVariantSrc, priority, alt,
}) => {
  // Hooks must run unconditionally every render (before the `!src` bailout
  // and regardless of `kind`, which can change across re-renders of the same
  // instance) — harmless no-op work for non-video/non-image kinds.
  const imageSrc = useProgressiveTierSrc({
    placeholder: variantPlaceholderSrc, small: variantSrc, large: variantLgSrc, original: src,
    // The caller passes the render-box width — the auto-quality display cap
    // (abrQuality.ts) stops a small block from fetching tiers it can't show.
    displayWidthPx: width,
  });
  // Seamless small→original swap (resumes playback position, never reloads
  // mid-play) — see useProgressiveTierVideoRef. No `large` video tier exists
  // yet, so this is a straight two-tier escalation.
  const videoRef = useProgressiveTierVideoRef({ small: videoVariantSrc, original: src, displayWidthPx: width });

  // A homepage is anyone's content on our origin: a `javascript:` src would run
  // in the PDF <iframe> / download <a> below (the server empties these on save
  // and on the public read; this is the last line).
  if (!src || !isSafeSiteUrl(src)) return null;
  const kind = getPreviewKind(mime || '', filename || '');

  switch (kind) {
    case 'model':
      return (
        <Suspense fallback={<Centered h={height}>Loading 3D model…</Centered>}>
          <ModelPreview
            // ModelPreview only reads original_filename (obj vs stl) + is_encrypted
            // (logging); the bytes are already public, so a minimal upload is safe.
            upload={{ original_filename: filename, is_encrypted: false } as unknown as MediaUpload}
            srcUrl={src}
            width={width}
            height={height}
            autoRotate={autoRotate}
            bgColor={bgColor}
          />
        </Suspense>
      );
    case 'image':
      return (
        <img
          src={imageSrc}
          alt={alt || filename}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          // react-dom on this project's pinned React 18 doesn't recognize the
          // camelCase `fetchPriority` prop (added natively in React 19) — it
          // would still land on the DOM element via React's unknown-attribute
          // fallback (browsers lowercase setAttribute names on HTML elements
          // regardless), but only after a dev-mode-only console warning.
          // Spelling it lowercase up front sidesteps that warning entirely.
          {...{ fetchpriority: priority ? 'high' : 'auto' }}
          style={{ width, maxWidth: '100%', height: 'auto', borderRadius: 8, display: 'block' }}
        />
      );
    case 'video': {
      // Unmuted autoplay is blocked by browsers, so autoplay implies muted.
      const videoStyle: React.CSSProperties = { ...boxStyle(width, height), minWidth: 0, display: 'block', background: '#000' };
      // `poster` is the instant recognizable placeholder for video (no
      // separate LQIP tier needed the way images have one) — it renders
      // natively the moment the element mounts, long before the small→
      // original staged tier (videoRef, above) resolves anything.
      const poster = posterVariantSrc || thumbUrlFromMediaSrc(src);
      if (videoAutoPlay) {
        return <AutoplayVideo videoRef={videoRef} depKey={src} poster={poster} controls={videoControls} loop={videoLoop} muted style={videoStyle} />;
      }
      return (
        <video
          ref={videoRef}
          poster={poster}
          controls={videoControls}
          loop={videoLoop}
          muted={videoMuted}
          playsInline
          preload="metadata"
          style={videoStyle}
        />
      );
    }
    case 'audio':
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width, maxWidth: '100%' }}>
          <WaveformAudioPlayer src={src} />
        </div>
      );
    case 'pdf':
      return <iframe src={src} title={filename} style={{ ...boxStyle(width, height), border: 0, background: '#fff' }} />;
    case 'presentation':
      return (
        <Suspense fallback={<Centered h={height}>Loading presentation…</Centered>}>
          <SlideDeckPreview
            // The pane reads id/version (render-session key) + filename
            // (format routing); the bytes are already public.
            upload={{ id: mediaIdFromSrc(src) ?? src, version: 0, original_filename: filename, is_encrypted: false } as unknown as MediaUpload}
            srcUrl={src}
            width={width}
            height={height}
          />
        </Suspense>
      );
    default:
      // No browser-renderable preview (psd/text/sheet/drawing/other) — offer the
      // file rather than a broken viewer.
      return (
        <Centered h={height}>
          <a href={src} download={filename || undefined} target="_blank" rel="noopener noreferrer"
            style={{ color: '#60a5fa', textDecoration: 'underline' }}>
            Download {filename || 'file'}
          </a>
        </Centered>
      );
  }
};

export default LibraryMediaView;
